import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Reveal, easeOut } from "@/components/Reveal";
import { PageHero } from "@/pages/FinishesPage";
import { PROCESS, STATS, IMG } from "@/lib/content";

const CHAPTERS = [
  { n: "01", t: "Concrete is not a floor. It's a canvas.", b: "Every LuxeBond project starts by reading the slab — its age, moisture, cracks, and history. The concrete tells us which system it will hold, and for how long. We never pour on a surface we haven't earned." },
  { n: "02", t: "The bond is mechanical, not hopeful.", b: "Diamond-grinding opens the pore structure so resin locks into the concrete rather than sitting on top of it. This single step is the difference between a floor that peels in two years and one that outlives the building." },
  { n: "03", t: "Resin has a clock. We respect it.", b: "Once catalyzed, the material is alive for minutes, not hours. Flake, metallic, quartz, or solid — the design has to be worked while it flows. That's why our crews move as one, and why we never over-book a pour day." },
  { n: "04", t: "Gloss is earned in the cure.", b: "A UV-stable topcoat seals the finish and locks the sheen. We let chemistry finish the job: full hardness and chemical resistance land in five to seven days, not the moment we leave." },
];

export default function ProcessPage() {
  const navigate = useNavigate();
  return (
    <>
      <PageHero
        eyebrow="How A Floor Is Made"
        title="Four stages, one continuous pour."
        body="Epoxy only performs if the sequence is right. This is the discipline behind every LuxeBond floor — the same on a single garage bay or a 60,000 sq ft plant."
      />

      {/* stage rail */}
      <section className="pb-8 pt-4" style={{ background: "var(--ink)" }}>
        <div className="wrap">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-9">
            {PROCESS.map((p, i) => (
              <Reveal key={p.num} delay={i * 0.08}>
                <div className="relative pt-7">
                  <span className="absolute top-0 left-0 h-[2px] w-full" style={{ background: "linear-gradient(90deg,var(--brass-bright),transparent)" }} />
                  <span className="font-mono-lbl text-[13px]" style={{ color: "var(--brass-bright)" }}>Stage {p.num}</span>
                  <h4 className="font-serif text-[20px] mt-3" style={{ color: "var(--champagne)" }}>{p.title}</h4>
                  <p className="text-[14px] leading-relaxed mt-3" style={{ color: "var(--stone)" }}>{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* manifesto chapters */}
      <section className="py-24" style={{ background: "var(--ink-2)" }}>
        <div className="wrap flex flex-col gap-px">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.05}>
              <div className="grid md:grid-cols-[160px_1fr] gap-8 py-12" style={{ borderTop: "1px solid var(--line)" }}>
                <div className="font-serif leading-none" style={{ fontSize: "clamp(56px,7vw,96px)", color: "var(--stone-dim)" }}>{c.n}</div>
                <div>
                  <h3 className="font-serif text-2xl sm:text-3xl max-w-[620px]" style={{ color: "var(--champagne)" }}>{c.t}</h3>
                  <p className="text-[15px] leading-relaxed mt-4 max-w-[620px]" style={{ color: "var(--stone)" }}>{c.b}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* spotlight image + stats */}
      <section className="py-24" style={{ background: "var(--ink)" }}>
        <div className="wrap grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <motion.div initial={{ scale: 1.06 }} whileInView={{ scale: 1 }} viewport={{ once: true }} transition={{ duration: 1.1, ease: easeOut }}
              className="relative aspect-[4/3] overflow-hidden" style={{ border: "1px solid var(--line)", boxShadow: "0 40px 80px -30px rgba(0,0,0,0.7)" }}>
              <img src={IMG.warehouse} alt="Industrial pour" className="w-full h-full object-cover" />
              <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 50% 40%, transparent 30%, rgba(12,11,9,0.55) 100%)" }} />
            </motion.div>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif text-3xl sm:text-4xl leading-[1.08]" style={{ color: "var(--champagne)" }}>
              Built to be walked on for a decade — and to look poured yesterday.
            </h2>
            <div className="grid grid-cols-3 gap-6 mt-10">
              {STATS.map((s) => (
                <div key={s.label}>
                  <div className="font-serif text-[34px] leading-none" style={{ color: "var(--brass-bright)" }}>{s.num}</div>
                  <div className="text-[11.5px] mt-2" style={{ color: "var(--stone)" }}>{s.label}</div>
                </div>
              ))}
            </div>
            <button onClick={() => { navigate("/"); setTimeout(() => document.getElementById("quote")?.scrollIntoView({ behavior: "smooth" }), 350); }} className="btn btn-primary mt-10" data-testid="process-quote">
              Start your project <ArrowRight size={16} />
            </button>
          </Reveal>
        </div>
      </section>
    </>
  );
}
